import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Search, Menu, X, ShieldCheck, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useState } from "react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navItems = [
    { to: "/", label: "Inicio" },
    { to: "/productos", label: "Productos" },
    { to: "/promociones", label: "Promociones" },
    { to: "/contacto", label: "Contacto" },
];

export default function Header() {
    const { user, logout } = useAuth();
    const { count, setOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const onSearchSubmit = (e) => {
        e.preventDefault();
        navigate(`/productos?search=${encodeURIComponent(search)}`);
        setMobileOpen(false);
    };

    return (
        <header
            data-testid="main-header"
            className="sticky top-0 z-40 w-full bg-mexyellow/95 backdrop-blur-xl border-b border-cocoa/10 shadow-sm"
        >
            <div className="fiesta-stripes h-1.5 w-full" />
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 flex items-center gap-2 sm:gap-4">
                <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
                    <img
                        src="/logo.png"
                        alt="La Dulcería Mexicana"
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shadow-md ring-2 ring-white"
                    />
                </Link>

                <Link
                    to="/"
                    data-testid="brand-tricolor"
                    className="flex items-center font-display font-extrabold tracking-tight text-sm sm:text-lg md:text-xl lg:text-2xl select-none whitespace-nowrap hover:opacity-90"
                    aria-label="Ir al inicio · ladulceriamexicana30"
                >
                    <span className="text-flaggreen">LADULC</span>
                    <span
                        className="text-white px-0.5"
                        style={{ textShadow: "0 1px 2px rgba(62,39,35,0.55), 0 0 1px rgba(62,39,35,0.9)" }}
                    >
                        ERIAMEX
                    </span>
                    <span className="text-flagred">ICANA30</span>
                </Link>

                <nav className="hidden md:flex items-center gap-6 ml-6">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-cocoa"
                                        : "text-cocoa/80 hover:text-cocoa"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <form onSubmit={onSearchSubmit} className="hidden lg:flex flex-1 max-w-sm ml-auto">
                    <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                            data-testid="search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar dulces..."
                            className="w-full pl-9 pr-4 py-2 rounded-full bg-muted/60 border border-transparent focus:bg-white focus:border-mexred outline-none text-sm transition-all"
                        />
                    </div>
                </form>

                <div className="flex items-center gap-2 ml-auto lg:ml-0">
                    <a
                        href="https://wa.me/56998091369?text=Hola%20La%20Dulcer%C3%ADa%20Mexicana%2C%20quiero%20hacer%20una%20consulta"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-whatsapp-btn"
                        className="p-2.5 rounded-full bg-green-500 hover:bg-green-600 transition-colors text-white shadow-sm"
                        aria-label="WhatsApp"
                        title="Escríbenos por WhatsApp"
                    >
                        <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                    </a>

                    <button
                        data-testid="cart-toggle-btn"
                        onClick={() => setOpen(true)}
                        className="relative p-2.5 rounded-full hover:bg-muted transition-colors"
                        aria-label="Carrito"
                    >
                        <ShoppingCart className="w-5 h-5 text-cocoa" />
                        {count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-mexred text-cocoa text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">
                                {count}
                            </span>
                        )}
                    </button>

                    {user && user.id ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="user-menu-btn"
                                    className="p-2.5 rounded-full hover:bg-muted transition-colors flex items-center gap-2"
                                >
                                    <div className="w-7 h-7 rounded-full bg-marigold text-cocoa text-xs font-bold flex items-center justify-center">
                                        {user.name?.[0]?.toUpperCase() || "U"}
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="font-medium">{user.name}</div>
                                    <div className="text-xs text-muted-foreground">{user.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    data-testid="menu-profile"
                                    onClick={() => navigate("/perfil")}
                                >
                                    <User className="w-4 h-4 mr-2" /> Mi perfil
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem
                                        data-testid="menu-admin"
                                        onClick={() => navigate("/admin")}
                                    >
                                        <ShieldCheck className="w-4 h-4 mr-2" /> Panel admin
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                                    <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Link
                            data-testid="login-btn"
                            to="/login"
                            className="hidden sm:inline-flex items-center gap-2 bg-mexred hover:bg-mexred_hover text-cocoa text-sm font-semibold px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md"
                        >
                            <User className="w-4 h-4" /> Entrar
                        </Link>
                    )}

                    <button
                        className="md:hidden p-2 rounded-full hover:bg-muted"
                        onClick={() => setMobileOpen((s) => !s)}
                        aria-label="Menú"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="md:hidden border-t border-border/60 bg-white">
                    <div className="px-4 py-3 flex flex-col gap-2">
                        <form onSubmit={onSearchSubmit} className="mb-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar dulces..."
                                    className="w-full pl-9 pr-4 py-2 rounded-full bg-muted/60 border border-transparent focus:bg-white focus:border-mexred outline-none text-sm"
                                />
                            </div>
                        </form>
                        {navItems.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `py-2 text-base font-medium ${
                                        isActive ? "text-cocoa" : "text-cocoa/80"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        {!user?.id && (
                            <Link
                                to="/login"
                                onClick={() => setMobileOpen(false)}
                                className="mt-2 bg-mexred text-cocoa rounded-full py-2 text-center font-semibold"
                            >
                                Entrar / Registrarse
                            </Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
